import ShareIcon from '@mui/icons-material/Share'
import * as Sentry from '@sentry/react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CopyIcon from '../../../assets/icons/copy.svg?react'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions } from '../../../store/hooks'
import Input from '../../Profile/Inputs/Input'
import { SHARE_TYPE, sharePuzzleFeedback } from '../functions/shareApi'
import getLinkIdFromUrl from '../helpers/getLinkIdFromUrl'

interface IProps {
    puzzleId: string
    shareType: SHARE_TYPE
    performanceGroupID: number
    onClose: () => void
}

const RESET_COPIED_TIMEOUT = 2000

const FeedbackShareActions: React.FC<IProps> = (props) => {
    const { puzzleId, performanceGroupID, shareType, onClose } = props
    const { isMobile } = useScreenSize()

    const setDrawerOpen = useStoreActions((state) => state.setDrawerOpen)
    const setIsDrawerMenuOpen = useStoreActions((state) => state.setIsDrawerMenuOpen)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)

    const [shareUrl, setShareUrl] = useState<string>()
    const [shareLoading, setShareLoading] = useState<boolean>(false)
    const [copied, setCopied] = useState<boolean>(false)

    const navigate = useNavigate()

    const onMenuHandler = () => {
        setIsDrawerMenuOpen(true)
        setDrawerOpen('open')
        navigate('/')
        onClose()
    }

    const onCopyHandler = async () => {
        if (shareUrl) {
            try {
                // rejects when clipboard-write is denied; throws where navigator.clipboard is missing
                await navigator.clipboard.writeText(shareUrl)
            } catch {
                setSnackbarMessage("Couldn't copy the link. Please select and copy it manually.")
                return
            }
            setCopied(true)
            setTimeout(() => setCopied(false), RESET_COPIED_TIMEOUT)
            analyticsManager.dispatchEvent('shareLinkCopied', {
                puzzleId: puzzleId,
                groupId: performanceGroupID,
                linkId: getLinkIdFromUrl(shareUrl),
                feature: shareType === SHARE_TYPE.DAILY_PUZZLE ? 'dailyPuzzle' : 'puzzleQuest',
            })
        }
    }

    const mobileSharing = async (url: string) => {
        if (isMobile && navigator.share) {
            try {
                await navigator.share({
                    title: shareType === SHARE_TYPE.DAILY_PUZZLE ? 'Daily Puzzle' : 'Puzzle Quest',
                    url: url,
                })
                analyticsManager.dispatchEvent('feedbackShared', {
                    puzzleId: puzzleId,
                    groupId: performanceGroupID,
                    linkId: getLinkIdFromUrl(url),
                    feature: shareType === SHARE_TYPE.DAILY_PUZZLE ? 'dailyPuzzle' : 'puzzleQuest',
                })
            } catch (error) {
                console.error(`[SHARE] Error when sharing ${shareType} feedback:`, error)
            }
        }
    }

    const onShareHandler = async () => {
        if (shareUrl && isMobile) {
            await mobileSharing(shareUrl)
        } else {
            setShareLoading(true)
            try {
                const result = await sharePuzzleFeedback({
                    shareType: shareType,
                    puzzleID: puzzleId || '',
                    performanceGroupID: performanceGroupID,
                })
                analyticsManager.dispatchEvent('shareClicked', {
                    puzzleId: puzzleId,
                    groupId: performanceGroupID,
                    linkId: result.shareURL ? getLinkIdFromUrl(result.shareURL) : '',
                    feature: shareType === SHARE_TYPE.DAILY_PUZZLE ? 'dailyPuzzle' : 'puzzleQuest',
                })

                if (result.shareURL) {
                    await mobileSharing(result.shareURL)
                    setShareUrl(result.shareURL)
                }
            } catch (error: any) {
                setSnackbarMessage("Couldn't create a share link. Please try again.")
                // sharePuzzleFeedback throws { error: { code } }; SOMETHING_WENT_WRONG = no response / non-JSON body
                const code = error?.error?.code
                if (!code) {
                    Sentry.captureException(error)
                } else if (code === 'SOMETHING_WENT_WRONG') {
                    Sentry.addBreadcrumb({ category: 'share', level: 'warning', message: `Share link failed: ${code}` })
                } else {
                    Sentry.captureException(new Error(`Share link failed: ${code}`), { fingerprint: ['share', code] })
                }
            } finally {
                setShareLoading(false)
            }
        }
    }

    // For desktop only
    if (!isMobile && (shareUrl || shareLoading)) {
        return (
            <>
                <Divider flexItem sx={{ borderColor: '#252636', borderWidth: '1px' }} />
                {shareUrl ? (
                    <Stack gap="16px" sx={{ alignSelf: 'stretch' }}>
                        <Input label="Link:" type="text" value={shareUrl} />
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<CopyIcon />}
                            onClick={onCopyHandler}
                            disabled={copied}
                            sx={{ alignSelf: 'center', minWidth: '200px' }}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </Stack>
                ) : (
                    <CircularProgress />
                )}
            </>
        )
    }

    return (
        <Stack direction="row" gap={{ xs: '8px', md: '16px' }}>
            <Button
                variant="outlined"
                size={isMobile ? 'medium' : 'large'}
                onClick={onMenuHandler}
                sx={{ minWidth: '135px' }}
            >
                Main menu
            </Button>
            <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<ShareIcon />}
                onClick={onShareHandler}
                sx={{ minWidth: '135px' }}
            >
                Share
            </Button>
        </Stack>
    )
}

export default FeedbackShareActions
